import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Megaphone, 
  ExternalLink, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Search,
  Filter,
  Grid3X3,
  List,
  Eye,
  Building2,
  Globe,
  Tag,
  Sparkles,
  ArrowRight,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BG_IMAGE = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

const Advertisements = () => {
  const { api } = useAuth();
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAd, setSelectedAd] = useState(null);

  // Categories aligned with AKIT's Islamic education focus
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'islamic_books', label: 'Islamic Books & Publishing' },
    { value: 'educational_materials', label: 'Educational Materials' },
    { value: 'halal_food', label: 'Halal Food & Beverages' },
    { value: 'islamic_clothing', label: 'Islamic Clothing & Attire' },
    { value: 'halal_finance', label: 'Halal Financial Services' },
    { value: 'travel_umrah', label: 'Travel & Umrah Services' },
    { value: 'technology', label: 'EdTech & Technology' },
    { value: 'health_wellness', label: 'Health & Wellness' },
    { value: 'events_training', label: 'Events & Training' },
    { value: 'other', label: 'Other Services' },
  ];

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const data = await api.getAdvertisements(true); // Only approved & active
      setAdvertisements(data || []);
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter advertisements
  const filteredAds = advertisements.filter(ad => {
    const matchesSearch = 
      ad.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.business_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || ad.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get category label
  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  // Ad Card Component
  const AdCard = ({ ad }) => (
    <div className="glass-card group rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-300">
      {/* Image Section */}
      <div className="relative h-48 bg-gradient-to-br from-primary/20 to-primary/5 overflow-hidden">
        {ad.image_url ? (
          <img 
            src={ad.image_url} 
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Building2 className="w-12 h-12 text-primary/30 mx-auto mb-2" />
              <span className="text-xs text-muted-foreground">{ad.business_name || 'Advertisement'}</span>
            </div>
          </div>
        )}
        
        {/* Category Badge */}
        {ad.category && (
          <span className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm text-foreground px-3 py-1 rounded-full text-xs font-medium border border-border">
            {getCategoryLabel(ad.category)}
          </span>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <Button
            onClick={() => setSelectedAd(ad)}
            className="btn-primary"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading font-bold text-lg text-foreground mb-1 line-clamp-1">
          {ad.title}
        </h3>
        
        {ad.business_name && (
          <p className="text-primary font-medium text-sm mb-3 flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            {ad.business_name}
          </p>
        )}
        
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {ad.description}
        </p>

        {/* Contact Icons */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {ad.email && (
            <span className="flex items-center gap-1 hover:text-primary transition-colors">
              <Mail className="w-3 h-3" />
              Email
            </span>
          )}
          {ad.phone && (
            <span className="flex items-center gap-1 hover:text-primary transition-colors">
              <Phone className="w-3 h-3" />
              Call
            </span>
          )}
          {ad.website && (
            <span className="flex items-center gap-1 hover:text-primary transition-colors">
              <Globe className="w-3 h-3" />
              Website
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(ad.created_at)}
          </span>
          <button
            onClick={() => setSelectedAd(ad)}
            className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1 transition-colors"
          >
            Details
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );

  // Ad List Row Component
  const AdRow = ({ ad }) => (
    <div className="glass-card rounded-xl p-4 flex gap-4 hover:border-primary/50 transition-colors">
      {/* Image */}
      <div className="w-28 h-28 flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5">
        {ad.image_url ? (
          <img 
            src={ad.image_url} 
            alt={ad.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-8 h-8 text-primary/30" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-foreground">{ad.title}</h3>
            {ad.business_name && (
              <p className="text-primary text-sm font-medium">{ad.business_name}</p>
            )}
          </div>
          {ad.category && (
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium flex-shrink-0">
              {getCategoryLabel(ad.category)}
            </span>
          )}
        </div>
        
        <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{ad.description}</p>
        
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {ad.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {ad.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(ad.created_at)}
            </span>
          </div>
          <Button
            onClick={() => setSelectedAd(ad)}
            size="sm"
            className="btn-primary"
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );

  // Ad Detail Modal
  const AdModal = ({ ad, onClose }) => (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="glass-card-heavy rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Image Header */}
        <div className="relative h-56 bg-gradient-to-br from-primary/20 to-primary/5">
          {ad.image_url ? (
            <img 
              src={ad.image_url} 
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Building2 className="w-16 h-16 text-primary/30 mx-auto mb-2" />
                <span className="text-muted-foreground">{ad.business_name || 'Advertisement'}</span>
              </div>
            </div>
          )}
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm hover:bg-background text-foreground w-10 h-10 rounded-full flex items-center justify-center transition-colors border border-border"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Category Badge */}
          {ad.category && (
            <span className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm text-foreground px-4 py-2 rounded-full text-sm font-medium border border-border">
              {getCategoryLabel(ad.category)}
            </span>
          )}
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">{ad.title}</h2>
          
          {ad.business_name && (
            <p className="text-primary font-semibold text-lg mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {ad.business_name}
            </p>
          )}
          
          <p className="text-muted-foreground leading-relaxed mb-6">{ad.description}</p>

          {/* Contact Information Card */}
          <div className="glass-card rounded-xl p-5 mb-6">
            <h3 className="font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              Contact Information
            </h3>
            <div className="grid gap-3">
              {ad.email && (
                <a 
                  href={`mailto:${ad.email}`}
                  className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <span>{ad.email}</span>
                </a>
              )}
              {ad.phone && (
                <a 
                  href={`tel:${ad.phone}`}
                  className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <span>{ad.phone}</span>
                </a>
              )}
              {ad.location && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <span>{ad.location}</span>
                </div>
              )}
              {ad.website && (
                <a 
                  href={ad.website.startsWith('http') ? ad.website : `https://${ad.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-primary hover:text-primary/80 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <span>Visit Website</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {ad.website && (
              <a
                href={ad.website.startsWith('http') ? ad.website : `https://${ad.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button className="w-full btn-primary h-12">
                  <Globe className="w-5 h-5 mr-2" />
                  Visit Website
                </Button>
              </a>
            )}
            {ad.email && (
              <a href={`mailto:${ad.email}`} className="flex-1">
                <Button variant="outline" className="w-full h-12">
                  <Mail className="w-5 h-5 mr-2" />
                  Send Email
                </Button>
              </a>
            )}
            {ad.phone && (
              <a href={`tel:${ad.phone}`} className="flex-1">
                <Button variant="outline" className="w-full h-12">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            )}
          </div>

          {/* Posted Date */}
          <p className="text-center text-muted-foreground text-sm mt-6">
            Posted on {formatDate(ad.created_at)}
          </p>
        </div>
      </div>
    </div>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading advertisements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Hero Section */}
      <section 
        className="relative py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-6">
            <Sparkles className="h-5 w-5" />
            <span className="text-sm font-medium">Halal Marketplace</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Featured <span className="text-primary">Advertisements</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg mb-8">
            Discover trusted Halal products and services from businesses that support 
            the Islamic education community in Kenya.
          </p>
          
          {/* CTA to Advertise */}
          <Link to="/advertise">
            <Button className="btn-primary">
              <Megaphone className="w-5 h-5 mr-2" />
              Advertise Your Business
            </Button>
          </Link>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl -mt-10">
        {/* Search & Filters Card */}
        <div className="glass-card-heavy rounded-2xl p-4 md:p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search advertisements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-glass pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5 pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-glass pl-10 pr-8 h-12 min-w-[200px] appearance-none cursor-pointer"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-background shadow text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Grid View"
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-background shadow text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title="List View"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing <span className="text-foreground font-medium">{filteredAds.length}</span> of{' '}
            <span className="text-foreground font-medium">{advertisements.length}</span> advertisements
          </div>
        </div>

        {/* Advertisements Display */}
        {filteredAds.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAds.map(ad => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAds.map(ad => (
                <AdRow key={ad.id} ad={ad} />
              ))}
            </div>
          )
        ) : (
          /* Empty State */
          <div className="glass-card rounded-2xl p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Megaphone className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              No Advertisements Found
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {searchTerm || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                : 'There are no advertisements to display at the moment. Check back soon!'}
            </p>
            {(searchTerm || selectedCategory !== 'all') && (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Call to Action Section */}
        <div className="mt-16 glass-card-heavy rounded-2xl p-8 md:p-12 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-4">
            <Megaphone className="h-5 w-5" />
            <span className="text-sm font-medium">Want to Advertise?</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
            Reach Thousands of IRE Teachers
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Promote your Halal business to Islamic Religious Education teachers across all 47 counties in Kenya. 
            Join our trusted network of advertisers today.
          </p>
          <Link to="/advertise">
            <Button className="btn-primary h-14 px-8 text-lg">
              <Megaphone className="w-5 h-5 mr-2" />
              Submit Advertisement Inquiry
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedAd && (
        <AdModal ad={selectedAd} onClose={() => setSelectedAd(null)} />
      )}
    </div>
  );
};

export default Advertisements;
